import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from '../../axiosConfig';
import '../../styles/SurveyPreviewModal.css';

const SurveyPreviewModal = ({ isOpen, onClose, survey, onDelete }) => {
    const { t } = useTranslation(['surveys', 'common']);
    const [assignedDogs, setAssignedDogs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isOpen && survey) {
            fetchAssignedDogs();
        }
    }, [isOpen, survey]);

    useEffect(() => {
        if (!isOpen) {
            setAssignedDogs([]);
        }
    }, [isOpen]);

    const fetchAssignedDogs = async () => {
        if (!survey || !survey.id) return;

        try {
            setIsLoading(true);
            const token = localStorage.getItem('jwt');

            // Fetch dogs assigned to this survey using the dedicated endpoint
            const response = await axios.get(`/api/v1/survey/${survey.id}/assigned-dogs`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setAssignedDogs(response.data || []);
        } catch (err) {
            console.error('Error fetching assigned dogs:', err);
            setAssignedDogs([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target.classList.contains('spm-overlay')) {
            onClose();
        }
    };

    const getQuestionTypeLabel = (type) => {
        const labels = {
            'TEXT': t('surveys:typeText'),
            'DATE': t('surveys:typeDate'),
            'SINGLE_CHOICE': t('surveys:typeSingleChoice'),
            'MULTIPLE_CHOICE': t('surveys:typeMultipleChoice'),
            'CHOICE_WITH_TEXT': t('surveys:typeChoiceWithText')
        };
        return labels[type] || type;
    };

    if (!isOpen || !survey) return null;

    const questions = survey.questions || [];
    const sortedQuestions = [...questions].sort((a, b) =>
        (a.questionIndex || 0) - (b.questionIndex || 0)
    );

    return (
        <div className="spm-overlay" onClick={handleOverlayClick}>
            <div className="spm-modal">
                {/* Header */}
                <div className="spm-header">
                    <h2 className="spm-title">{survey.title}</h2>
                    <p className="spm-subtitle">{t('surveys:questions')} ({questions.length})</p>
                </div>

                {/* Content */}
                <div className="spm-content">
                    {/* Questions List */}
                    <div className="spm-questions">
                        {sortedQuestions.length === 0 ? (
                            <p className="spm-no-questions">{t('surveys:noQuestions')}</p>
                        ) : (
                            sortedQuestions.map((question, index) => (
                                <div key={question.id || index} className="spm-question">
                                    <div className="spm-question-header">
                                        <span className="spm-question-number">{index + 1}.</span>
                                        <span className="spm-question-text">{question.questionText}</span>
                                    </div>

                                    {question.questionType && question.questionType !== 'TEXT' && (
                                        <div className="spm-question-meta">
                                            <span className="spm-question-type">
                                                {getQuestionTypeLabel(question.questionType)}
                                            </span>
                                        </div>
                                    )}

                                    {/* Show answer options for choice questions */}
                                    {(question.questionType === 'SINGLE_CHOICE' ||
                                            question.questionType === 'MULTIPLE_CHOICE' ||
                                            question.questionType === 'CHOICE_WITH_TEXT') &&
                                        question.answerOptions && question.answerOptions.length > 0 && (
                                            <div className="spm-question-options">
                                                <span className="spm-options-label">{t('surveys:availableOptions')}</span>
                                                <ul className="spm-options-list">
                                                    {question.answerOptions.map((option, optIndex) => (
                                                        <li key={optIndex} className="spm-option-item">
                                                            {option}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                </div>
                            ))
                        )}
                    </div>

                    {/* Assigned Dogs Section */}
                    <div className="spm-assigned-section">
                        <h3 className="spm-assigned-title">{t('surveys:assignedTo')}</h3>

                        {isLoading ? (
                            <p className="spm-assigned-loading">{t('surveys:assignedLoading')}</p>
                        ) : assignedDogs.length === 0 ? (
                            <p className="spm-assigned-empty">{t('surveys:notAssigned')}</p>
                        ) : (
                            <div className="spm-assigned-list">
                                {assignedDogs.map(dog => (
                                    <div key={dog.id} className="spm-assigned-dog">
                                        <div className="spm-dog-info">
                                            <span className="spm-dog-name">{dog.name}</span>
                                            <span className="spm-dog-breed">{dog.breed || t('surveys:noBreed')}</span>
                                        </div>
                                        {(dog.ownersName || dog.ownerName) && (
                                            <div className="spm-dog-owner">
                                                <span className="spm-owner-label">{t('surveys:owner')}</span>
                                                <span className="spm-owner-value">{dog.ownersName || dog.ownerName}</span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="spm-footer">
                    <button className="spm-delete-btn" onClick={onDelete}>
                        {t('surveys:deleteSurvey')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SurveyPreviewModal;
